import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../context/DataContext.js';
import DateRangePicker from './DateRangePicker.js';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import StateFilter from './StateFilter.js';
import { Transition } from '@headlessui/react';

export default function EventFilter({ isOpen, onClose, onFilterApply, activeFilters }) {
  const { events: countryFilteredEvents, selectedCountry } = useData();
  
  const defaultFilters = useMemo(() => ({
    dateRange: { start: null, end: null },
    state: [],
    venue: [],
    promoter: [],
    type: []
  }), []);
  
  const [filters, setFilters] = useState(activeFilters || defaultFilters);
  
  const defaultOptions = useMemo(() => ({
    state: [],
    venue: [],
    promoter: [],
    type: []
  }), []);
  
  const [options, setOptions] = useState(defaultOptions);
  
  // Update filters when activeFilters changes (e.g., when country changes)
  useEffect(() => {
    setFilters(activeFilters || defaultFilters);
  }, [activeFilters, defaultFilters]);

  const [expandedSections, setExpandedSections] = useState({
    type: true,
    state: true,
    venue: false,
    organiser: false
  });

  useEffect(() => {
    if (countryFilteredEvents && countryFilteredEvents.length > 0) {
      // Always get all available states
      const stateOptions = [...new Set(countryFilteredEvents.map(event => event.StateNameShort))].filter(Boolean).sort();
      
      // Filter venues based on selected states
      let venueOptions = [];
      if (filters.state.length > 0) {
        // Only show venues from selected states
        venueOptions = [...new Set(
          countryFilteredEvents
            .filter(event => filters.state.includes(event.StateNameShort))
            .map(event => event.VenueName)
        )].filter(Boolean).sort();
      } else {
        // Show all venues if no states are selected
        venueOptions = [...new Set(countryFilteredEvents.map(event => event.VenueName))].filter(Boolean).sort();
      }

      setOptions({
        state: stateOptions,
        venue: venueOptions,
        promoter: [...new Set(countryFilteredEvents.map(event => event.BusinessName))].filter(Boolean).sort(),
        type: [...new Set(countryFilteredEvents.map(event => event.EventType))].filter(Boolean).sort()
      });
      
      // Clear venue selections if they're no longer available in the selected states
      if (filters.venue.length > 0) {
        setFilters(prev => ({
          ...prev,
          venue: prev.venue.filter(v => venueOptions.includes(v))
        }));
      }
    } else {
      setOptions(defaultOptions);
    }
  }, [countryFilteredEvents, defaultOptions, filters.state, filters.venue.length]);

  const filterTypes = [
    { key: 'type', label: 'Type', section: 'type' },
    //{ key: 'state', label: 'State', section: 'state' },
    { key: 'venue', label: 'Venue', section: 'venue' },
    { key: 'promoter', label: 'Promoter', section: 'organiser' }
  ];

  const handleApplyClick = () => {
    onFilterApply(filters);
    onClose();
  };

  const hasAppliedFilters = Boolean(
    (filters.dateRange?.start && filters.dateRange?.end) ||
    filters.state.length > 0 ||
    filters.venue.length > 0 ||
    filters.promoter.length > 0 ||
    filters.type.length > 0
  );

  const handleClearClick = () => {
    setFilters(defaultFilters);
    onFilterApply(null);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const AccordionHeader = ({ title, section }) => (
    <button
      onClick={() => toggleSection(section)}
      className="flex w-full items-center justify-between py-2 text-sm font-medium text-gray-900 hover:text-gray-600"
    >
      {title}
      {expandedSections[section] ? (
        <ChevronUpIcon className="h-4 w-4" />
      ) : (
        <ChevronDownIcon className="h-4 w-4" />
      )}
    </button>
  );

  return (
    <Transition
      show={isOpen}
      enter="transition-opacity duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div 
        className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50"
        onClick={onClose}
      >
        <Transition
          show={isOpen}
          enter="transition-transform duration-300 ease-out"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transition-transform duration-200 ease-in"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <div 
            className="fixed top-16 left-0 bottom-0 w-96 bg-white shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
        {/* Header */}
        <div className="flex justify-between items-center p-6 pb-4 border-b">
          <h3 className="text-lg font-medium">Event Filters</h3>
          <button 
            onClick={onClose}
            className="text-red-600 hover:text-red-700 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-2">
            {/* Date Range Picker */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Requires two dates, from and to. Click clear to reset.
              </p>
              <DateRangePicker
                onChange={(dateRange) => {
                  setFilters(prev => ({
                    ...prev,
                    dateRange
                  }));
                }}
                initialDateRange={filters.dateRange}
              />
            </div>
            <div className="mb-2">
              <AccordionHeader title="State" section="state" />
              {expandedSections.state && (
                <StateFilter
                  countryName={selectedCountry?.CountryName}
                  availableStates={options.state}
                  selectedStates={filters.state}
                  events={countryFilteredEvents}
                  onChange={(newStates) => {
                    setFilters(prev => ({
                      ...prev,
                      state: newStates
                    }));
                  }}
                />
              )}
            </div>
            {/* Checkbox Filters */}
            {filterTypes.map(({ key, label, section }) => (
              <div key={key} className="mb-2">
                <AccordionHeader title={label} section={section} />
                {expandedSections[section] && (
                  <div className="space-y-2 max-h-40 overflow-y-auto mt-2">
                    {options[key].map(value => {
                      // Count events matching this filter value
                      const eventCount = countryFilteredEvents?.filter(event => {
                        if (key === 'type') return event.EventType === value;
                        if (key === 'venue') return event.VenueName === value;
                        if (key === 'promoter') return event.BusinessName === value;
                        return false;
                      }).length || 0;
                      
                      return (
                        <label key={value} className="flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-slate-600 focus:ring-slate-600"
                              checked={filters[key].includes(value)}
                              onChange={() => {
                                setFilters(prev => ({
                                  ...prev,
                                  [key]: prev[key].includes(value)
                                    ? prev[key].filter(item => item !== value)
                                    : [...prev[key], value]
                                }));
                              }}
                            />
                            <span className="ml-2 text-sm text-gray-600">{value}</span>
                          </div>
                          <span className="text-xs text-gray-500 ml-2">({eventCount})</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Fixed Bottom Buttons */}
        <div className="p-6 bg-white border-t sticky bottom-0">
          <div className="flex justify-between gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-red-600 bg-white border border-red-600 rounded hover:bg-red-50"
            >
              Close
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleClearClick}
                className={`px-4 py-2 text-sm rounded border ${
                  hasAppliedFilters
                    ? 'text-white bg-slate-900 border-slate-900 hover:bg-slate-700'
                    : 'text-slate-700 bg-white border-slate-700 hover:bg-slate-50'
                }`}
              >
                Clear
              </button>
              <button
                onClick={handleApplyClick}
                className="px-4 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
          </div>
        </Transition>
      </div>
    </Transition>
  );
}
